import dayjs from 'dayjs';
import {
  Select,
  MenuItem,
  TextField,
  Button,
  ButtonGroup,
  Typography,
  AppHeader,
  Grid,
  Form,
  useFirstNameValidator,
  usePhoneNumberValidator,
  useSelectValidator,
  RadioGroup,
  Radio,
  RadioTypography,
  useRadioGroupValidator,
  Stack,
  useEmailAddressValidator,
  useLastNameValidator,
  useBirthDateValidator,
  Link,
  Box,
  DateField,
} from '@ace-clublabs/shared-component-library';
import { useContext, JSX, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Pages } from '@lib/types';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { GenderListItem, getGenderList, getProgressProps } from '../utils/helpers';
import { getNextPage } from '../utils/getNextPage';
import { calculateNewCursorPosition, TextInputChangeEvent, MobileNumberInputType } from '../utils/formatters';
import { UtagType } from '../utils/UtagHelper';
import { FlowType, RoleType } from '@ace-clublabs/common-membership-api-types';
import { NewMembershipFormData } from '../application/models/NewJoinSessionData';
import { AppProgressHeader } from '../components/AppProgressHeader';
import { getMembershipTitle } from '../utils/helpers';

interface ContactDetailsProps {}

export const ContactDetailsPage: (props: ContactDetailsProps) => JSX.Element = (props) => {
  const { sessionData, membershipInfo, refreshSession, loadingInfo, sendTag, updateMembershipForm } =
    useContext(MembershipAppStateContext);

  const { formData } = sessionData;
  const phoneRef = useRef(null);

  const history = useHistory();
  const genderList = getGenderList(membershipInfo);

  const [isTagging, setIsTagging] = useState(true);

  const { membershipLevel } = formData;

  const getInitialContactState = (): Partial<NewMembershipFormData> => {
    return {
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      dateOfBirth: formData.dateOfBirth ?? null,
      gender: formData.gender,
      email: formData.email || '',
      mobileNumber: formData.mobileNumber || '',
      mobileConsent: formData.mobileConsent,
    };
  };

  const tag = () => {
    const tagPage = sessionData.flowType === FlowType.GIFT ? 'membership:gift:recipient' : 'primary member';
    sendTag(UtagType.view, tagPage);
    setIsTagging(false);
  };

  const [contactForm, setContactFormData] = useState(getInitialContactState());
  const [cursorPosition, setCursorPosition] = useState(0);

  const isGiftFlow = sessionData.flowType === FlowType.GIFT;

  const primaryRole = membershipInfo?.roles.find((role) => role.role === RoleType.PRIMARY);

  const firstNameValidator = useFirstNameValidator({
    maxLength: 20,
  });

  const lastNameValidator = useLastNameValidator({
    maxLength: 20,
  });

  const emailValidator = useEmailAddressValidator({
    required: !isGiftFlow,
  });

  const genderValidator = useSelectValidator({ validationFeedback: 'immediate' });
  const phoneNumberValidator = usePhoneNumberValidator();
  const radioValidator = useRadioGroupValidator();

  const birthdayValidator = useBirthDateValidator({
    minAge: primaryRole?.minimumAge,
    maxAge: primaryRole?.maximumAge,
    required: !isGiftFlow,
  });

  const progressProps = getProgressProps(sessionData.flowType!, Pages.ContactDetailsPage);

  const defaultTitle = <>Let’s set up your {getMembershipTitle(membershipLevel)} member account</>;

  const title = isGiftFlow ? 'Who is the gift recipient?' : defaultTitle;
  const subtitle = isGiftFlow
    ? 'We need information about the recipient.'
    : 'We need some basic information to create your membership.';

  const submitForm: () => void = () => {
    history.push(getNextPage(Pages.ContactDetailsPage, sessionData.flowType));
  };

  const handleMobileNumberFieldChange = (event: TextInputChangeEvent): void => {
    const updatedCursorPosition = refreshMobileNumberFieldCursorPositionAfterChangeEvent(event);
    setCursorPosition(updatedCursorPosition);
    setContactFormData((prev) => ({ ...prev, mobileNumber: event.currentTarget.value }));
  };

  const refreshMobileNumberFieldCursorPositionAfterChangeEvent = (event: TextInputChangeEvent): number => {
    const { inputType } = event.nativeEvent as InputEvent;
    // @ts-ignore
    const inputNode = phoneRef?.current.childNodes[1].childNodes[0];
    const previousCursorPosition = cursorPosition ?? 0;
    const changeEventCursorPosition = inputNode?.selectionStart ?? 0;

    const updatedCursorPosition = calculateNewCursorPosition(
      inputType as MobileNumberInputType,
      previousCursorPosition,
      changeEventCursorPosition
    );

    queueMicrotask(() => inputNode.setSelectionRange(updatedCursorPosition, updatedCursorPosition));

    return updatedCursorPosition;
  };

  const handleMobileNumberFieldSelectionEvent = (event: React.SyntheticEvent<HTMLInputElement, Event>): void => {
    // @ts-ignore
    const inputNode = phoneRef?.current.childNodes[1].childNodes[0];
    const selectionEventCursorPosition = inputNode.selectionStart;
    setCursorPosition(selectionEventCursorPosition ?? 0);
  };

  useEffect(() => {
    updateMembershipForm({ ...sessionData.formData, ...contactForm });
  }, [contactForm]);

  useEffect(() => {
    refreshSession();
    if (!isGiftFlow && sessionData.formData.mobileConsent == null) {
      setContactFormData((prev) => ({ ...prev, mobileConsent: true }));
    }
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo?.benefits) {
      return;
    }
    tag();
    //sendTag(UtagType.view, 'primary member');
    setIsTagging(false);
  }, [membershipInfo]);

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null;
  if (isLoading) {
    return (
      <div>
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </div>
    );
  }

  if (isTagging) {
    return <></>;
  }

  const saveGenerateAutoLead = (generateAutoLead: boolean) => {
    const updatedFormData = {
      ...sessionData.formData,
      generateAutoLead,
    };
    updateMembershipForm(updatedFormData);
  };

  const emailAndPhone = [
    <TextField
      key={4}
      fullWidth
      name="emailAddress"
      aria-label="emailAddress"
      value={contactForm.email}
      label={`Email address${isGiftFlow ? ' (optional)' : ''}`}
      id="emailAddress"
      validator={emailValidator}
      showClearButton={true}
      onChange={(v) => setContactFormData((prev) => ({ ...prev, email: v.target.value }))}
      onClearButtonClick={() => {
        setContactFormData((prev) => ({ ...prev, email: '' }));
      }}
      sx={{
        '& .MuiFormLabel-asterisk': {
          display: 'none',
        },
      }}
    />,
    <TextField
      key={5}
      ref={phoneRef}
      label="Mobile number"
      id="mobileNumber"
      defaultValue={contactForm.mobileNumber}
      validator={phoneNumberValidator}
      for="phone-number"
      showClearButton={true}
      onChange={handleMobileNumberFieldChange}
      onClearButtonClick={() => {
        setContactFormData((prev) => ({ ...prev, mobileNumber: '' }));
      }}
      InputProps={{
        componentsProps: {
          input: {
            onSelect: handleMobileNumberFieldSelectionEvent,
          },
        },
      }}
      fullWidth
      required
      sx={{
        '& .MuiFormLabel-asterisk': {
          display: 'none',
        },
      }}
    />,
  ];

  if (isGiftFlow) {
    emailAndPhone.reverse();
  }

  return (
    <>
      <AppProgressHeader {...progressProps} styleOverrides={{ marginBottom: '1rem' }} />

      <Stack gap={'1rem'}>
        <Typography variant="h5" className="contact-details-header-title">
          {title}
        </Typography>
        <Typography variant="b1Regular" className="contact-details-header-text">
          {subtitle}
        </Typography>
      </Stack>

      <div className="contact-details-form qm-mask">
        <Form onSubmit={submitForm}>
          <Stack gap={'1rem'}>
            <TextField
              key={1}
              defaultValue={contactForm.firstName}
              label="First name"
              for="first-name"
              id="firstName"
              fullWidth
              required
              validator={firstNameValidator}
              showClearButton={true}
              onChange={(v) => {
                setContactFormData((prev) => ({ ...prev, firstName: v.currentTarget.value }));
              }}
              onClearButtonClick={() => {
                setContactFormData((prev) => ({ ...prev, firstName: '' }));
              }}
              sx={{
                '& .MuiFormLabel-asterisk': {
                  display: 'none',
                },
              }}
            />
            <TextField
              key={2}
              defaultValue={contactForm.lastName}
              label="Last name"
              for="last-name"
              id="lastName"
              fullWidth
              required
              validator={lastNameValidator}
              showClearButton={true}
              onChange={(v) => {
                setContactFormData((prev) => ({ ...prev, lastName: v.currentTarget.value }));
              }}
              onClearButtonClick={() => {
                setContactFormData((prev) => ({ ...prev, lastName: '' }));
              }}
              sx={{
                '& .MuiFormLabel-asterisk': {
                  display: 'none',
                },
              }}
            />
            {membershipInfo?.enableDobField && (
              <DateField
                name="dateOfBirth"
                label="Date of birth"
                id="dateOfBirth"
                helperText="mm/dd/yyyy"
                fullWidth
                onClearButtonClick={() => {
                  setContactFormData((prev) => ({ ...prev, dateOfBirth: null }));
                }}
                validator={birthdayValidator}
                value={
                  contactForm.dateOfBirth === '' || contactForm.dateOfBirth == null
                    ? null
                    : dayjs(contactForm.dateOfBirth)
                }
                onChange={(v) => {
                  setContactFormData((prev) => ({
                    ...prev,
                    dateOfBirth: v?.format('MM/DD/YYYY') ?? null,
                  }));
                }}
                sx={{ marginTop: '0.5rem' }}
              />
            )}
            <Select
              key={3}
              label="Gender"
              id="genderDropdown"
              displayEmpty
              placeholder="Select one"
              helperText={`Must match state driver’s license or I.D.`}
              fullWidth={true}
              value={genderList.find((g) => {
                return g.genderValue === contactForm.gender;
              })}
              getDisplayText={(v: any) => {
                return v.genderName;
              }}
              validator={genderValidator}
              required
              onChange={(v) => {
                const gender = v.target.value as GenderListItem;
                const genderValue = gender.genderValue;
                setContactFormData((prev) => ({ ...prev, gender: genderValue }));
              }}
            >
              {genderList.map((gender, i) => (
                <MenuItem key={gender.genderName} value={gender as any}>
                  {gender.genderName}
                </MenuItem>
              ))}
            </Select>

            {emailAndPhone}
          </Stack>

          {!isGiftFlow && (
            <Grid order={3}>
              <Stack className="mobile-consent" marginTop={'2rem'}>
                <Typography variant="h5Medium" className="mobile-consent-title">
                  Mobile consent agreement
                </Typography>
                <Box className="terms-section" sx={{ margin: '0.5rem 0' }}>
                  <Typography variant="b3Regular" sx={{ color: '#6C7175' }}>
                    I agree to calls and texts, using automated technology, from or on AAA's behalf regarding membership
                    and insurance products/services and offers. Consent is not a condition of purchase. Message and data
                    rates may apply. I agree to the{' '}
                    <Link
                      className="privacy-notice-link"
                      href={`https://www.ace.aaa.com/legal/privacy.html`}
                      target="_blank"
                      variant="b3Regular"
                    >
                      Privacy Notice
                    </Link>
                    ,{' '}
                    <Link
                      className="terms-and-condition-link"
                      href={`https://www.ace.aaa.com/legal/terms-of-use.html`}
                      target="_blank"
                      variant="b3Regular"
                    >
                      Terms & Conditions
                    </Link>
                    , and{' '}
                    <Link
                      className="text-terms-link"
                      href={`https://www.ace.aaa.com/legal/text-messaging-programs-terms-and-conditions.html`}
                      target="_blank"
                      variant="b3Regular"
                    >
                      Text Terms
                    </Link>
                    .
                  </Typography>
                </Box>
                <Stack className="consent-radio-button">
                  <RadioGroup
                    variant="text"
                    row
                    id={`consent-radio-button`}
                    name={`MobileConsent`}
                    value={
                      contactForm.mobileConsent === true
                        ? 'agree'
                        : contactForm.mobileConsent === false
                          ? 'disagree'
                          : null
                    }
                    validator={radioValidator}
                    fullWidth
                    sx={{ width: '380px' }}
                  >
                    <Radio
                      name={`agree Mobile Consent`}
                      value={`agree`}
                      id="agreeMobileConsent"
                      label={
                        <RadioTypography sx={{ fontSize: '1rem !important', color: '#404040' }}>
                          I agree
                        </RadioTypography>
                      }
                      onClick={() => {
                        setContactFormData((prev) => ({ ...prev, mobileConsent: true }));
                        saveGenerateAutoLead(membershipInfo?.insuranceLeadGeneration?.preselected);
                      }}
                      sx={{ maxWidth: '155px !important' }}
                    />
                    <Radio
                      name={`disagree Mobile Consent`}
                      value={`disagree`}
                      id="disagreeMobileConsent"
                      label={
                        <RadioTypography sx={{ fontSize: '1rem !important', color: '#404040' }}>
                          I don’t agree
                        </RadioTypography>
                      }
                      onClick={() => {
                        setContactFormData((prev) => ({ ...prev, mobileConsent: false }));
                        saveGenerateAutoLead(false);
                      }}
                    />
                  </RadioGroup>
                </Stack>
              </Stack>
            </Grid>
          )}
          <div className="contact-details-btn">
            <ButtonGroup sx={{ marginTop: isGiftFlow ? '1.5rem' : '0' }}>
              <Button id="nextButton" type="submit" className="btn-large" label="Next" />
            </ButtonGroup>
          </div>
        </Form>
      </div>
    </>
  );
};
